import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useClub } from '@/contexts/ClubContext';
import { useI18n } from '@/contexts/I18nContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Target, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import StarRating from '@/components/StarRating';
import { ApiClubMember, Member } from '@/types';
import PageSpinner from '@/components/PageSpinner';
import PackageFeatureGate from '@/components/PackageFeatureGate';
import { CardGridSkeleton, PageHeaderSkeleton } from '@/components/PageSkeletons';
import { billingService, feedbackService, memberService } from '@/services/storageService';
import { getLatestEvaluation, mapEvaluationsFromApi, mapMembersFromApi } from '@/lib/clubApiMappers';

export default function Evaluations() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeClubId, currentClubRole } = useClub();
  const clubId = activeClubId ? Number(activeClubId) : null;

  const membersQuery = useQuery({
    queryKey: ['club-members', clubId],
    queryFn: () => memberService.list(clubId as number) as Promise<ApiClubMember[]>,
    enabled: Boolean(clubId),
  });
  const billingQuery = useQuery({
    queryKey: ['billing-status', clubId],
    queryFn: () => billingService.getStatus(clubId),
    enabled: Boolean(user && clubId),
  });

  const baseMembers = useMemo(
    () => mapMembersFromApi((membersQuery.data || []) as ApiClubMember[]),
    [membersQuery.data],
  );

  const [memberEvals, setMemberEvals] = useState<Record<string, any[]>>({});

  const members = useMemo(() => {
    return baseMembers.map((member) => ({
      ...member,
      evaluations: mapEvaluationsFromApi(memberEvals[member.id] || []),
    }));
  }, [baseMembers, memberEvals]);

  const currentMember = user ? members.find((m) => m.id === user.id || m.userId === user.id) : undefined;

  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [isEvalDialogOpen, setIsEvalDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [ratingFilter, setRatingFilter] = useState<'all' | 'not_rated' | '4' | '3' | '2' | '1'>('all');
  const [editingEvaluationId, setEditingEvaluationId] = useState<string | null>(null);
  const [showEvaluations, setShowEvaluations] = useState(true);
  const [evalListMode, setEvalListMode] = useState<'3' | '10' | 'all'>('3');
  const [showEvalForm, setShowEvalForm] = useState(false);

  const [evalData, setEvalData] = useState({
    categories: {
      technical: 3,
      movement: 3,
      tactical: 3,
      physical: 3,
      psychological: 3,
    },
    quarterPlan: '',
  });

  const [page, setPage] = useState(1);
  const pageSize = 12;

  const clampRating = (val: number) => {
    if (!Number.isFinite(val)) return 0;
    const rounded = Math.round(val * 10) / 10;
    return Math.min(5, Math.max(0, rounded));
  };

  const computeRating = (member: Member) => {
    const latest = getLatestEvaluation(member.evaluations);
    const hasRating = member.hasEvaluation || (member.evaluations?.length ?? 0) > 0;
    return { value: hasRating ? latest?.rating ?? null : null, hasRating };
  };

  const normalizeRole = (role?: string | null) => String(role ?? '').trim().toLowerCase();
  const memberRoleForUser = useMemo(() => {
    if (!user) return null;
    const list = (membersQuery.data || []) as ApiClubMember[];
    const match = list.find(
      (m) =>
        String(m.member_id) === String(user.id) ||
        (m.email && user.email && m.email.toLowerCase() === user.email.toLowerCase()),
    );
    return match?.role ? String(match.role).toLowerCase() : null;
  }, [membersQuery.data, user]);
  const roleForAccess = normalizeRole(memberRoleForUser || currentClubRole || user?.role || null);
  const canUpgradePackage = roleForAccess === 'admin';
  const resolvedPackageCode = String(
    billingQuery.data?.package || billingQuery.data?.recommended_package || 'package1',
  )
    .trim()
    .toLowerCase();
  const isEvaluationsLockedByPackage = resolvedPackageCode === 'package1';

  const clubEvalsQuery = useQuery({
    queryKey: ['club-evals', clubId],
    queryFn: () => feedbackService.listForClub(clubId as number),
    enabled:
      Boolean(clubId) &&
      !isEvaluationsLockedByPackage &&
      (roleForAccess === 'admin' || roleForAccess === 'coach'),
  });

  useEffect(() => {
    if (isEvaluationsLockedByPackage) {
      setMemberEvals({});
      return;
    }
    const grouped: Record<string, any[]> = {};
    (clubEvalsQuery.data || []).forEach((ev: any) => {
      const id = String(ev.player_id ?? ev.playerId ?? '');
      if (!id) return;
      if (!grouped[id]) grouped[id] = [];
      grouped[id].push(ev);
    });
    setMemberEvals(grouped);
  }, [clubEvalsQuery.data, isEvaluationsLockedByPackage]);

  const players = useMemo(
    () =>
      members
        .map((m) => ({
          ...m,
          role: normalizeRole(m.role) as Member['role'],
          originalRole: normalizeRole((m as any).originalRole),
        }))
        .filter((m) => {
          const role = m.role;
          const raw = (m as any).originalRole as string | undefined;
          const isSelf = user && m.userId === user.id;
          const isCoachTarget = role.includes('coach') || (raw && raw.includes('coach'));
          const isAdminTarget = role.includes('admin') || (raw && raw.includes('admin'));

          // Never evaluate yourself
          if (isSelf) return false;
          if (!role) return false;
          if (isCoachTarget) return false;

          // Admins are only evaluable by coaches
          if (isAdminTarget) return roleForAccess === 'coach';

          return role === 'player' || role === 'member' || role === 'admin';
        })
        // ✅ SORT BY NAME (ASCENDING)
        .sort((a, b) => {
          const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
          const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
          return nameA.localeCompare(nameB);
        }),
    [members, roleForAccess, user],
  );

  const filteredPlayers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return players.filter((player) => {
      const name = `${player.firstName} ${player.lastName}`.trim().toLowerCase();
      const nameMatch = !q || name.includes(q);
      if (!nameMatch) return false;
      if (ratingFilter === 'all') return true;
      const { value, hasRating } = computeRating(player);
      if (ratingFilter === 'not_rated') return !hasRating || value === null || value === undefined;
      const target = Number(ratingFilter);
      if (!hasRating || value === null || value === undefined) return false;
      return Math.round(value) === target;
    });
  }, [players, ratingFilter, searchQuery]);

  if (!clubId) {
    return <PageSpinner label="Select a club to manage evaluations." />;
  }

  if (membersQuery.isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <PageHeaderSkeleton />
        <CardGridSkeleton count={6} className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4" />
      </div>
    );
  }

  // Show evaluable members. Coaches are excluded; admins can be evaluated by coaches.

  const canEvaluate = () => roleForAccess === 'admin' || roleForAccess === 'coach';

  if (!canEvaluate()) {
    return (
      <div className="text-center py-12">
        <Target className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">Only coaches and admins can access evaluations.</p>
      </div>
    );
  }

  const getEvaluatorName = () => {
    if (currentMember) return `${currentMember.firstName} ${currentMember.lastName}`;
    return user?.email || 'Admin';
  };

  const getEvaluatorId = () => currentMember?.id || user?.id || 'admin';

  const handleAddEvaluation = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMember) return;
    if (!clubId) {
      toast.error('Select a club first');
      return;
    }

    const categoryValues = Object.values(evalData.categories);
    const avg = categoryValues.reduce((sum, val) => sum + val, 0) / categoryValues.length;
    const rating = Math.max(1, Math.min(5, Math.round(avg * 10) / 10));

    (async () => {
      try {
        if (editingEvaluationId) {
          await feedbackService.update(editingEvaluationId, {
            technical_skills: evalData.categories.technical,
            movement: evalData.categories.movement,
            tactical_ability: evalData.categories.tactical,
            physical_attributes: evalData.categories.physical,
            psychological_factors: evalData.categories.psychological,
            quarter_plan: evalData.quarterPlan,
            comments: evalData.quarterPlan,
          });
        } else {
          await feedbackService.submit({
            club_id: clubId,
            player_id: Number(selectedMember.id),
            technical_skills: evalData.categories.technical,
            movement: evalData.categories.movement,
            tactical_ability: evalData.categories.tactical,
            physical_attributes: evalData.categories.physical,
            psychological_factors: evalData.categories.psychological,
            quarter_plan: evalData.quarterPlan,
            comments: evalData.quarterPlan,
          });
        }

        const fresh = await feedbackService.listForPlayer(selectedMember.id, clubId);
        setMemberEvals((prev) => ({ ...prev, [selectedMember.id]: fresh as any[] }));
        toast.success(editingEvaluationId ? 'Evaluation updated!' : 'Evaluation added!');
        setIsEvalDialogOpen(false);
        setEditingEvaluationId(null);
      } catch (err: any) {
        toast.error(err?.message || 'Failed to save evaluation');
      }
    })();
  };


  const openEvalDialog = (member: Member) => {
    const role = normalizeRole(member.role);
    if (role !== 'player' && role !== 'member' && role !== 'admin') return;
    setSelectedMember(member);
    setEditingEvaluationId(null);
    setShowEvaluations(true);
    setEvalListMode('3');
    setShowEvalForm(false);
    (async () => {
      if (clubId) {
        try {
          const evals = await feedbackService.listForPlayer(member.id, clubId);
          setMemberEvals((prev) => ({ ...prev, [member.id]: evals as any[] }));
        } catch {
          // ignore
        }
      }

      const latest = member.evaluations?.[0];
      if (latest) {
        setEvalData({
          categories: {
            technical: latest.categories?.technical ?? 3,
            movement: latest.categories?.movement ?? 3,
            tactical: latest.categories?.tactical ?? 3,
            physical: latest.categories?.physical ?? 3,
            psychological: latest.categories?.psychological ?? 3,
          },
          quarterPlan: latest.quarterPlan || latest.feedback || '',
        });
      } else {
        setEvalData({
          categories: {
            technical: 3,
            movement: 3,
            tactical: 3,
            physical: 3,
            psychological: 3,
          },
          quarterPlan: '',
        });
      }
      setIsEvalDialogOpen(true);
    })();
  };

  const evaluationsForSelected = selectedMember
    ? (() => {
        const raw = memberEvals[selectedMember.id];
        if (raw) return mapEvaluationsFromApi(raw);
        return selectedMember.evaluations || [];
      })()
    : [];

  const canManageEvaluation = (evaluation: { coachId?: string }) =>
    roleForAccess === 'admin' || (roleForAccess === 'coach' && evaluation.coachId === user?.id);

  const handleEditEvaluation = (evaluationId: string) => {
    const evaluation = evaluationsForSelected.find((ev) => ev.id === evaluationId);
    if (!evaluation) return;
    setEditingEvaluationId(evaluationId);
    setShowEvalForm(true);
    setEvalData({
      categories: {
        technical: clampRating(Number(evaluation.categories?.technical ?? 0)),
        movement: clampRating(Number(evaluation.categories?.movement ?? 0)),
        tactical: clampRating(Number(evaluation.categories?.tactical ?? 0)),
        physical: clampRating(Number(evaluation.categories?.physical ?? 0)),
        psychological: clampRating(Number(evaluation.categories?.psychological ?? 0)),
      },
      quarterPlan: evaluation.quarterPlan || evaluation.feedback || '',
    });
  };

  const resetEvalForm = () => {
    setEditingEvaluationId(null);
    setShowEvalForm(false);
    setEvalData({
      categories: {
        technical: 3,
        movement: 3,
        tactical: 3,
        physical: 3,
        psychological: 3,
      },
      quarterPlan: '',
    });
  };

  const visibleEvaluations = (() => {
    if (!showEvaluations) return [];
    if (evalListMode === 'all') return evaluationsForSelected;
    const limit = evalListMode === '10' ? 10 : 3;
    return evaluationsForSelected.slice(0, limit);
  })();

  const handleDeleteEvaluation = async (evaluationId: string) => {
    if (!selectedMember || !clubId) return;
    if (!window.confirm(t('evaluation_delete_confirm', 'Delete this evaluation? This cannot be undone.'))) return;
    try {
      await feedbackService.delete(evaluationId);
      const fresh = await feedbackService.listForPlayer(selectedMember.id, clubId);
      setMemberEvals((prev) => ({ ...prev, [selectedMember.id]: fresh as any[] }));
      toast.success('Evaluation deleted');
      if (editingEvaluationId === evaluationId) resetEvalForm();
    } catch (err: any) {
      toast.error(err?.message || t('delete_evaluation_failed', 'Failed to delete evaluation'));
    }
  };


  return (
    <div className="relative min-h-[calc(100vh-8rem)]">
      <div
        className={`space-y-6 animate-fade-in transition-all ${
          isEvaluationsLockedByPackage ? 'blur-[6px] pointer-events-none select-none' : ''
        }`}
      >
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground flex items-center gap-3">
            <Target className="text-primary" />
            Member Evaluations
          </h1>
          <p className="text-muted-foreground mt-1">Evaluate players and admins</p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Input
            placeholder="Search members..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            className="sm:max-w-xs"
          />
          <Select
            value={ratingFilter}
            onValueChange={(value) => {
              setRatingFilter(value as typeof ratingFilter);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="Filter rating" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All ratings</SelectItem>
              <SelectItem value="not_rated">Not evaluated</SelectItem>
              <SelectItem value="4">4 stars</SelectItem>
              <SelectItem value="3">3 stars</SelectItem>
              <SelectItem value="2">2 stars</SelectItem>
              <SelectItem value="1">1 star</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredPlayers.slice((page - 1) * pageSize, page * pageSize).map((player) => (
            <Card key={player.id}>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between">
                  <span>
                    {player.firstName} {player.lastName}
                  </span>
                  {(() => {
                    const { value, hasRating } = computeRating(player);
                    if (!hasRating || value === null || value === undefined) {
                      return (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <StarRating value={0} size={14} />
                          <span>Not rated</span>
                        </div>
                      );
                    }
                    return <StarRating value={value} size={14} />;
                  })()}
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-4">

                <div className="flex gap-2 pt-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => openEvalDialog(player)}>
                    <Plus size={14} className="mr-1" /> Evaluate
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredPlayers.length > pageSize && (
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, filteredPlayers.length)} of {filteredPlayers.length}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                Prev
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => (p * pageSize < filteredPlayers.length ? p + 1 : p))}
                disabled={page * pageSize >= filteredPlayers.length}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {filteredPlayers.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <Target className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No players to evaluate. Add players first!</p>
            </CardContent>
          </Card>
        )}

        {/* Evaluation Dialog */}
        <Dialog open={isEvalDialogOpen} onOpenChange={setIsEvalDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                Evaluate {selectedMember?.firstName} {selectedMember?.lastName}
              </DialogTitle>
              <DialogDescription>
                Rate each category and add a quarter plan for this player.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleAddEvaluation} className="space-y-4">
              {evaluationsForSelected.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Existing Evaluations</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowEvaluations((prev) => !prev)}
                    >
                      {showEvaluations ? 'Collapse' : 'Expand'}
                    </Button>
                  </div>

                  {showEvaluations && (
                    <>
                      <div className="space-y-2 max-h-48 overflow-auto rounded-md border p-2">
                        {visibleEvaluations.map((ev) => (
                          <div key={ev.id} className="flex items-center justify-between gap-2 rounded-md border px-2 py-1">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 text-sm">
                                <span className="font-medium truncate">{ev.coachName || 'Admin'}</span>
                                <span className="text-muted-foreground text-xs">
                                  {ev.date ? new Date(ev.date).toLocaleString() : ''}
                                </span>
                              </div>
                              <StarRating value={ev.rating} size={12} />
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditEvaluation(ev.id)}
                                disabled={!canManageEvaluation(ev)}
                              >
                                Edit
                              </Button>
                              <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDeleteEvaluation(ev.id)}
                                disabled={!canManageEvaluation(ev)}
                              >
                                Delete
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>Showing</span>
                        <Button
                          type="button"
                          variant={evalListMode === '3' ? 'secondary' : 'ghost'}
                          size="sm"
                          onClick={() => setEvalListMode('3')}
                        >
                          Last 3
                        </Button>
                        <Button
                          type="button"
                          variant={evalListMode === '10' ? 'secondary' : 'ghost'}
                          size="sm"
                          onClick={() => setEvalListMode('10')}
                        >
                          Last 10
                        </Button>
                        <Button
                          type="button"
                          variant={evalListMode === 'all' ? 'secondary' : 'ghost'}
                          size="sm"
                          onClick={() => setEvalListMode('all')}
                        >
                          View all
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between">
                <Label>{editingEvaluationId ? 'Edit Evaluation' : 'New Evaluation'}</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    resetEvalForm();
                    setShowEvalForm(true);
                  }}
                >
                  Add new evaluation
                </Button>
              </div>

              {showEvalForm && (
                <>
                  <div className="space-y-2">
                    <Label>Category Ratings (1-5)</Label>
                    <div className="space-y-3">
                      {(
                        [
                          { key: 'technical', label: 'Technical Skills (Grip & Stroke)' },
                          { key: 'movement', label: 'Movement and Footwork' },
                          { key: 'tactical', label: 'Tactical Ability (Offense & Defense)' },
                          { key: 'physical', label: 'Physical Attributes' },
                          { key: 'psychological', label: 'Psychological Factors' },
                        ] as Array<{ key: keyof typeof evalData.categories; label: string }>
                      ).map((cat) => (
                        <div key={cat.key} className="flex items-center justify-between gap-4">
                          <div className="text-sm">{cat.label}</div>
                          <div className="flex items-center gap-3">
                            <Input
                              type="number"
                              min={0}
                              max={5}
                              step={0.1}
                              value={evalData.categories?.[cat.key] ?? 0}
                              onChange={(e) => {
                                const next = clampRating(Number(e.target.value));
                                setEvalData((prev) => ({
                                  ...prev,
                                  categories: { ...prev.categories, [cat.key]: next },
                                }));
                              }}
                              className="w-20"
                            />
                            <StarRating value={evalData.categories?.[cat.key]} size={16} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Quarter Plan</Label>
                    <Textarea
                      value={evalData.quarterPlan}
                      onChange={(e) => setEvalData((prev) => ({ ...prev, quarterPlan: e.target.value }))}
                      placeholder="Plan to improve over the next quarter..."
                      rows={3}
                      required
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <Button type="submit" className="flex-1">
                      {editingEvaluationId ? 'Update Evaluation' : 'Submit Evaluation'}
                    </Button>
                    <Button type="button" variant="outline" onClick={resetEvalForm}>
                      {editingEvaluationId ? 'Cancel Edit' : 'Cancel'}
                    </Button>
                  </div>
                </>
              )}
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isEvaluationsLockedByPackage && (
        <div className="absolute inset-0 z-20 bg-background/30 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6">
          <PackageFeatureGate
            featureName="Member Evaluations"
            currentPackageCode={resolvedPackageCode}
            canUpgrade={canUpgradePackage}
            onUpgrade={() => navigate('/dashboard/club')}
          />
        </div>
      )}
    </div>
  );
}
